'use client'

import { useEffect, useRef, useState } from 'react'

interface PlayAudioButtonProps {
  src: string
  ariaLabel?: string
  size?: number
  mobileSize?: number
}

export default function PlayAudioButton({
  src,
  ariaLabel = 'Audio abspielen',
  size = 54, // desktop size
  mobileSize = 42, // mobile size
}: PlayAudioButtonProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    // detect mobile screen size
    const checkScreen = () => setIsMobile(window.innerWidth < 640)
    checkScreen()
    window.addEventListener('resize', checkScreen)
    return () => window.removeEventListener('resize', checkScreen)
  }, [])

  useEffect(() => {
    const audio = new Audio(src)
    audio.preload = 'none'
    audioRef.current = audio

    const onEnded = () => setIsPlaying(false)
    audio.addEventListener('ended', onEnded)

    return () => {
      audio.pause()
      audio.removeEventListener('ended', onEnded)
      audioRef.current = null
    }
  }, [src])

  const toggle = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      try {
        await audio.play()
        setIsPlaying(true)
      } catch (e) {
        console.error('Audio play error:', e)
      }
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={ariaLabel}
      className="inline-flex cursor-pointer items-center justify-center rounded-full bg-white/95 shadow-md ring-2 ring-black/50 transition hover:scale-105 hover:ring-black/20"
      style={{
        width: isMobile ? mobileSize : size,
        height: isMobile ? mobileSize : size,
      }}
    >
      {isPlaying ? (
        // Pause icon
        <svg
          width={isMobile ? 18 : 24}
          height={isMobile ? 18 : 24}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <rect x="5" y="4" width="4" height="16" rx="1" />
          <rect x="15" y="4" width="4" height="16" rx="1" />
        </svg>
      ) : (
        // Play icon
        <svg
          width={isMobile ? 18 : 24}
          height={isMobile ? 18 : 24}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M8 5v14l11-7-11-7z" />
        </svg>
      )}
    </button>
  )
}
